import { styled } from '@mui/material/styles';
import { Box, Button, Typography, CircularProgress } from '@mui/material';

const BaseLabel = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '14px',
});

const BaseInfoText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '14px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const BaseSubText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
});

const BaseIcon = styled(Box)({
  fontSize: 14,
  marginRight: 8,
});

export const ErrorIcon = styled(BaseIcon)({ color: 'var(--error)' });
export const RejectionIcon = styled(BaseIcon)({ color: 'var(--error)' });
export const IconBox = styled(Box)({
  fontSize: 16,
  color: 'var(--primary-blue)',
  marginRight: 8,
});
export const DeliveryIcon = styled(Box)({
  fontSize: 16,
  color: 'var(--primary-blue)',
  marginRight: 8,
});

// ========== Label Styles ==========
export const TitleInfo = styled(BaseLabel)({ color: 'var(--instructions)' });
export const LabelText = styled(BaseLabel)({ color: 'var(--instructions)' });
export const ApprovalLabel = styled(BaseLabel)({
  color: 'var(--instructions)',
});
export const RejectionLabel = styled(BaseLabel)({
  color: 'var(--error)',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
});

export const InfoText = styled(BaseInfoText)({ color: 'var(--primary-navy)' });
export const ApprovalText = styled(InfoText)({ maxWidth: 200 });
export const DeliveryAddressText = styled(InfoText)({ maxWidth: 280 });
export const RejectionText = styled(InfoText)({});
export const InfoTextMax200 = styled(InfoText)({ maxWidth: 200 });
export const InfoTextMax160 = styled(InfoText)({ maxWidth: 160 });
export const InfoTextMax120 = styled(InfoText)({ maxWidth: 120 });

export const ContactLabel = styled(BaseSubText)({
  color: 'var(--primary-blue)',
  marginBottom: 8,
});
export const OrgName = styled(BaseSubText)({
  color: 'var(--primary-navy)',
  marginTop: 8,
});
export const Subtitle = styled(BaseSubText)({ color: 'var(--instructions)' });
export const DeliveryTitle = styled(BaseSubText)({
  color: 'var(--primary-brand-blue)',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
export const SuspendedReasonText = styled(BaseSubText)({
  fontSize: 16,
  color: 'var(--subtext)',
  display: 'inline',
});
export const ContactInfo = styled(BaseSubText)({
  color: 'var(--primary-navy)',
  marginBottom: 4,
  display: 'flex',
  alignItems: 'center',
});

export const SecondaryButton = styled(Button)({
  fontFamily: 'Outfit',
  fontSize: '16px',
  fontWeight: 500,
  width: 180,
  padding: '8px',
  backgroundColor: 'var(--primary-blue)',
  color: 'var(--bg-color)',
  borderRadius: '8px',
  border: '1px solid var(--border-blue)',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'var(--hover-blue)',
  },
});

export const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'smScreen',
})<{ smScreen: boolean }>(({ theme, smScreen }) => ({
  position: 'relative',
  marginLeft: smScreen ? 0 : theme.spacing(6),
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(2),
}));

export const TitleRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingRight: '24px',
});

export const TitleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'smScreen',
})<{ smScreen?: boolean }>(({ smScreen }) => ({
  fontFamily: 'Lufga',
  fontWeight: 500,
  fontSize: smScreen ? '24px' : '32px',
  color: 'var(--primary-blue)',
  maxWidth: 'calc(100% - 80px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ContactBox = styled(Box)({
  marginTop: 16,
  backgroundColor: '#FAFAFA',
  height: 100,
  width: 400,
  borderRadius: 8,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

export const SuspendedReasonBox = styled(Box)({
  marginTop: 32,
  marginBottom: 48,
  border: '1px solid var(--error)',
  width: 600,
  borderRadius: 10,
  padding: 10,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export const InfoBlock = styled(Box)({ marginBottom: 16 });

export const DeliveryAddressBox = styled(Box)({
  padding: 16,
  width: 294,
  border: '1px solid var(--inactive-btn)',
  borderRadius: 8,
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  boxShadow: '0 1px 1px rgba(0, 0, 0, 0.1)',
});

export const FlexRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'flex',
  gap: 16,
  width: fullWidth ? '100%' : '628px',
  alignItems: 'stretch',
}));

export const FlexRowGap = styled(Box)({
  display: 'flex',
  gap: 24,
  alignItems: 'stretch',
  flexWrap: 'nowrap',
});

export const FlexColumnEnd = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
});

export const ApprovalBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'fullWidth',
})<{ status: string; fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(2),
  width: fullWidth ? '100%' : 595,
  border: '1px solid var(--inactive-btn)',
  borderRadius: 8,
  flexGrow: 0.8,
  backgroundColor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: 'auto',
  boxSizing: 'border-box',
}));

export const ApprovalRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});

export const ApprovalInfoColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const ApprovalStatusBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
});

export const RejectionNoteBox = styled(Box)({
  marginTop: 16,
});

export const FormTitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: 'var(--primary-navy)',
});

export const FormContentBox = styled(Box)({
  marginLeft: 40,
  marginRight: 40,
});

export const ButtonContainerBox = styled(Box)({
  marginLeft: 40,
  marginTop: 24,
  marginBottom: 16,
});

export const ButtonContainerBoxGap = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
  marginBottom: '16px',
  marginLeft: '40px',
});

export const StatusTitleBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
});

export const ContentLayoutBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'ml' &&
    prop !== 'mr' &&
    prop !== 'mt' &&
    prop !== 'display' &&
    prop !== 'gridTemplateColumns' &&
    prop !== 'gap',
})<{
  ml?: number | string;
  mr?: number | string;
  mt?: number | string;
  display?: string;
  gridTemplateColumns?: string;
  gap?: number;
}>(({ ml, mr, mt, display, gridTemplateColumns, gap }) => ({
  marginLeft: typeof ml === 'number' ? ml * 8 : ml,
  marginRight: typeof mr === 'number' ? mr * 8 : mr,
  marginTop: typeof mt === 'number' ? mt * 8 : mt,
  display: display || 'block',
  gridTemplateColumns,
  gap: gap ? gap * 8 : undefined,
  height: '100%',
}));

export const StatusSelectPlaceholder = styled(Box)({
  color: 'var(--instructions)',
});

export const DialogContentBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'ml' &&
    prop !== 'mr' &&
    prop !== 'mt' &&
    prop !== 'display' &&
    prop !== 'gridTemplateColumns' &&
    prop !== 'gap' &&
    prop !== 'height' &&
    prop !== 'px',
})<{
  ml?: number;
  mr?: number;
  mt?: number;
  display?: string;
  gridTemplateColumns?: string;
  gap?: number;
  height?: string;
  px?: number;
}>(
  ({
    ml = 0,
    mr = 0,
    mt = 0,
    display = 'block',
    gridTemplateColumns,
    gap,
    px = 0,
  }) => ({
    marginLeft: ml * 8,
    marginRight: mr * 8,
    marginTop: mt * 8,
    paddingLeft: px ? px * 8 : undefined,
    paddingRight: px ? px * 8 : undefined,
    display,
    gridTemplateColumns,
    gap: gap ? gap * 8 : undefined,
  })
);

export const SubmitButtonBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ml' && prop !== 'mt' && prop !== 'mb',
})<{ ml?: number; mt?: number; mb?: number }>(({ ml = 0, mt = 0, mb = 0 }) => ({
  marginLeft: ml * 8,
  marginTop: mt * 8,
  marginBottom: mb * 8,
}));

export const FlexContainerBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const FlexRowStartBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginBottom: 15,
});

export const ResetContainerBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginTop: 24,
});

export const ResetPasswordRowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
});

export const FlexColumnBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 40,
  flex: 1,
  marginBottom: 10,
});

export const FlexColumnBoxGrn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginBottom: 10,
});

export const BorderedSectionBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: 16,
});

export const SectionTitleTypography = styled(Typography)({
  marginBottom: 16,
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: 'var(--primary-navy)',
});

export const ItemRowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  marginBottom: 5,
  '&:last-child': {
    marginBottom: -20,
  },
});

export const ItemFieldBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 40,
});

export const ItemFieldAlignedBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

export const RemoveItemBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  marginLeft: 16,
  marginBottom: 32,
});

export const StyledCircularProgress = styled(CircularProgress)({
  color: 'var(--bg-color)',
});

export const FlexColumnGapBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gap',
})<{ gap?: number }>(({ gap = 2 }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: gap * 8,
}));

export const FlexRowSpaceBetweenBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width?: number | string }>(({ width }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: typeof width === 'number' ? width : width,
}));

export const FlexBoxTextLeft = styled(Box)({
  textAlign: 'left',
});

export const RelativePositionBox = styled(Box)({
  position: 'relative',
});

export const ItemFieldAlignedBoxMl2 = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginLeft: 16,
});

export const TrashIcon = styled(Box)({
  fontSize: '18px',
  color: 'var(--instructions)',
  cursor: 'pointer',
  '&:hover': {
    color: 'var(--error)',
  },
});

export const ResetFlexRowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginTop: 20,
});
